import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    nav: {
      home: 'Home',
      ecosystem: 'Ecosystem',
      about: 'About',
      contact: 'Contact',
      dropdown: {
        products: 'Products',
        services: 'Services',
        platform: 'Software-Defined Motion Platform',
        applications: 'Domains & Applications',
        company: 'Our Company',
        team: 'Our Team',
        facilities: 'Our Facilities',
        partners: 'Our Partners',
      },
    },
    footer: {
      quickLinks: 'Quick Links',
      socials: 'Socials',
      links: {
        home: 'Home',
        ecosystem: 'Ecosystem',
        about: 'About',
        contact: 'Contact',
      },
      copyright: '© 2026 Takumi Motion Controls Pvt. Ltd. All Rights Reserved.',
      terms: 'Terms of use',
      privacy: 'Privacy policy',
      japanAddress: ['402 Dai-ni Kyoto Bldg.,', '227 Daikoku-cho,', 'Shichijo-dori Aburanokoji Higashi-iru,', 'Shimogyo-ku, Kyoto 600-8223, Japan'],
    },
    home: {
      hero: {
        line1: 'Driving the Future with',
        line2: 'Motion Control',
        subtitle: 'Rooted in Indian innovation, engineered with Japanese technology.',
        explore: 'Explore Solutions',
        getInTouch: 'Get in touch',
      },
      artOfMotion: {
        eyebrow: 'What we do',
        heading: 'The Art of Motion',
        description: 'Takumi is a next-gen motion products company, powered by Indian innovation and Japanese technology, seamlessly blending Art & Engineering.',
        cta: 'Our Story',
      },
      case: {
        eyebrow: 'What we stand for',
        heading: 'Democratizing CASE Mobility',
        description: 'Connected, Autonomous, Shared, and Electric mobility should not be a privilege, it should be accessible, scalable, and built for everyone. Takumi builds the motion control technology that makes this possible.',
        connect: 'Connected',
        autonomous: 'Autonomous',
        shared: 'Shared/Smart',
        electric: 'Electric',
      },
      ecosystem: {
        eyebrow: 'Our ecosystem',
        heading: 'Powering the future of mobility',
        description: 'Everything Takumi builds — products, services, applications, and platforms designed to work together as one intelligent motion control ecosystem. Explore what powers the future of mobility.',
        products: { title: 'Products', tags: ['HVAC controller', 'EV controllers', 'T1 controller', 'HMI controller', 'Controller'] },
        platform: { title: 'Software-Defined Motion Platform', tags: ['Intelligent motion', 'Innovative IPs', 'Plug-N-Play Models'] },
        domains: { title: 'Domains & applications', tags: ['EV Vehicles', 'Drones', 'Fork-lift', 'Conveyer'] },
        services: { title: 'Services', tags: ['PoC & Prototype Build', 'Motor-Controller Calibration', 'Tear-down & Benchmarking', 'Value Analysis & Value Engineering'] },
      },
      transport: {
        eyebrow: 'At our core',
        heading: 'Precision Motion Control, for every application',
        description: 'Built across industries. Engineered with expertise. One motion control company behind it all.',
        sectionTitle: 'Transportation applications',
        seeAll: 'See all',
        apps: ['Electric Scooter', 'Electric cycle', 'Electric Car', 'Electric Tuk-tuk', 'Forklift', 'Drone'],
      },
      industrial: {
        sectionTitle: 'Industrial applications',
        seeAll: 'See all',
        apps: ['AGV/AMR', 'Robotic Arm', 'Conveyer', 'Treadmill', 'Telecom RET', 'Industrial Pump'],
      },
      whatsNew: {
        eyebrow: "Know what's new",
        heading: "Precision Engineering for Tomorrow's Mobility",
        description: 'From electric vehicles to industrial automation, our motion control solutions power the technologies that are shaping the future of transportation and manufacturing.',
        powerRange: 'Ready Power range',
        voltageRange: 'Upcoming voltage range',
        cta: 'Explore Solutions',
      },
      partners: {
        investors: 'Investors',
        techPartners: 'Technology Partners',
        clients: 'Clients',
      },
      cta: {
        heading: 'Ready to Power Your Vision?',
        description: "Whether it's a product, a platform, or a partnership — let's talk.",
        button: 'Contact us',
      },
    },
    about: {
      banner: {
        heading1: 'About',
        heading2: 'Takumi',
        description: 'Takumi is a next-gen motion products company, powered by Indian innovation and Japanese technology, seamlessly blending Art & Engineering.',
      },
      mission: {
        title: 'Our Mission',
        quote: 'To create a robust ecosystem for Mass Custom Engineering of Intelligent Motion Products at Speed and Scale for societal technology adoption.',
        belief: 'We believe in the power of combining Indian innovation with Japanese technology to create solutions that not only meet but exceed global standards in motion control engineering.',
      },
      vision: {
        title: 'Our Vision',
        description: 'Harmonizing product development & distribution away from the traditional tiered setup to create a more accessible and efficient mobility ecosystem.',
      },
      case: {
        title: 'Democratizing CASE Mobility',
        connect: 'Connected',
        autonomous: 'Autonomous',
        shared: 'Shared/Smart',
        electric: 'Electric',
      },
      team: {
        eyebrow: 'Our Team',
        heading: 'The People of Takumi',
        description: 'Integrated System/Product Engineering teams with specialization in Model Based System Engineering, Hardware Engineering, Software Engineering, PoC/Prototype Manufacturing, System Integration – Calibration – Validation.',
        peopleEyebrow: 'The people',
        peopleHeading: '40+ Minds Powering Motion',
        peopleDescription: 'A team bound by one mission: democratizing intelligent motion control. We bring together diverse talent, decades of combined expertise, and an unwavering commitment to building products that matter.',
      },
      facilities: {
        eyebrow: 'Our Facilities',
        heading: 'Find us at',
        description: 'From concept to production, our facilities support every stage of motion controller development.',
        upcoming: 'Upcoming',
        locations: [
          { name: 'Takumi Bengaluru', badge: 'Bengaluru, India', desc: 'Engineering and PoC & Proto Production' },
          { name: 'Takumi Kyoto',     badge: 'Kyoto, Japan',     desc: 'Technology Partnerships · Market Research' },
          { name: 'Takumi Tenkasi',   badge: 'Tenkasi, India',   desc: 'Engineering, Production & Test Lab' },
          { name: 'Takumi Chennai',   badge: 'Chennai, India',   desc: 'Upcoming' },
        ],
      },
      partners: {
        eyebrow: 'Our partners',
        heading: 'Collaborating for Scale',
        description: 'We collaborate with industry leaders in automotive, robotics, and technology to expand capabilities, reach new markets and solve complex challenges.',
        semiconductor: 'Semiconductor Partners',
        software: 'Software & Tools Partners',
        investors: 'Investors in Takumi',
        fccYear: '2022',
        fccDesc: 'FCC made an early-stage strategic investment in Takumi Motion Controls Pvt. Ltd.',
        zohoYear: '2026',
        zohoDesc: 'Zoho made strategic investment in Takumi Motion Controls Pvt. Ltd.',
      },
      cta: {
        heading: 'Interested in working with us',
        description: 'Drop a message and our team will connect with you in no time.',
        button: 'Contact us',
      },
    },
    ecosystemPage: {
      hero: {
        line1: 'Powering the future',
        line2: 'of motion with',
        subtitle: 'A complete ecosystem engineered to work as one intelligent system.',
        rotatingWords: ['Products', 'Services', 'Platform', 'Applications'],
      },
      tabs: {
        products: 'Products',
        services: 'Services',
        applications: 'Domains & Applications',
        platform: 'Software-Defined Motion Platform',
      },
      products: {
        eyebrow: 'Products',
        heading: 'Our Core Products',
        description: 'In-house developed motion control products powered by our Software-Defined Motion Platform.',
        viewDetails: 'View details',
        partnerTitle: 'Our Partner Products',
        partnerDescription: 'Curated partner ecosystem products to complement your motion control solutions.',
        coreProducts: [
          {
            kicker: 'Motor Controllers',
            title: 'Intelligent Vehicle Control Unit (iVCU)',
            description: 'iVCU is the centralized brain of the vehicle, coordinating motor control, safety interlocks, and system-wide diagnostics in real time.',
            tags: ['Two-wheelers', 'Three-wheelers', 'Agricultural vehicles', 'Light off-road'],
          },
          {
            kicker: 'Telematics Controllers',
            title: 'Telematics Control Unit (TCU)',
            description: 'TCU is a compact embedded gateway that turns connected two-wheelers into live data sources — capturing motion, GNSS location, and vehicle state and streaming it to Takumi Cloud over LTE/MQTT.',
            tags: ['Two-wheelers', 'Three-wheelers', 'Light off-road', 'Fleet and Logistics'],
          },
          {
            kicker: 'Motor + Motor Controllers',
            title: 'Motor Control Unit (MCU)',
            description: 'MCU delivers sensorless and encoder-based motor control with a wide voltage/power range, built for high-efficiency, software-defined drive systems.',
            tags: ['Two-wheelers', 'Three-wheelers', 'Light off-road', 'Fleet and Logistics'],
          },
        ],
        partnerProducts: [
          { title: 'Sensors',                      items: ['LiDAR', 'RADAR', 'Ultrasonic', 'Position Sensor', '+2 more'] },
          { title: 'HMI (Human Machine Interface)', items: ['Console/display', 'Brake', 'Throttle', 'Steering', '+2 more'] },
          { title: 'Battery + BMS',                 items: ['Up to 400V / 100 KWH', 'Lithium-ion', 'Smart BMS'] },
          { title: 'Accessories',                   items: ['Custom Wire Harness', 'Enclosures', 'Connectors'] },
        ],
      },
      services: {
        eyebrow: 'Services',
        heading: 'Our Core Services',
        description: 'End-to-end engineering services to accelerate your product development.',
        items: [
          { title: 'Takumi Software-Defined Motion Platform', description: 'Complete platform for motion control product development.',        features: ['Digital Twin', 'Model Based Design', 'Online IDE', 'Runtime Services'] },
          { title: 'PoC & Prototype Build',              description: 'Rapid prototyping and proof of concept development.',               features: ['Fast Turnaround', 'Design Validation', 'Iteration Support', 'Production Ready'] },
          { title: 'Motor-Controller Calibration',       description: 'Expert calibration services for optimal performance.',               features: ['Performance Tuning', 'Efficiency Optimization', 'Thermal Management', 'Safety Compliance'] },
          { title: 'Tear-down & Benchmarking',           description: 'Competitive analysis and product benchmarking.',                    features: ['Component Analysis', 'Cost Breakdown', 'Technology Assessment', 'Recommendations'] },
          { title: 'Value Analysis & Value Engineering', description: 'Optimize cost while maintaining quality.',                          features: ['Should-Costing', 'Design Optimization', 'Supplier Negotiation', 'BOM Reduction'] },
          { title: 'Product Qualification & Certification', description: 'Certification and homologation support from our partner network.', features: ['Compliance Testing', 'Regulatory Certification', 'Homologation', 'Documentation'] },
        ],
      },
      applications: {
        eyebrow: 'Domains & Applications',
        transportTitle: 'Transportation applications',
        industrialTitle: 'Industrial applications',
        seeAll: 'See all',
        transportApps: ['Electric Scooter', 'Electric Bicycle', 'Electric Car', 'Electric Tuk-tuk', 'Forklift', 'Drone'],
        industrialApps: ['AGV/AMR', 'Robotic Arm', 'Conveyer', 'Treadmill', 'Telecom RET', 'Industrial Pump'],
      },
      platform: {
        eyebrow: 'Software-Defined Motion Platform',
        heading: 'Complete Platform for Motion Control Development',
        description: 'A modern development and runtime platform that connects digital models, runtime services, and the full Takumi ecosystem.',
        features: [
          { title: 'Intelligent Motion',             description: 'Application Specific – Software Defined – Platform Engineered' },
          { title: 'Innovative IPs',                 description: 'Motor Control Algorithms & Software, Digital Power, Sensor Fusion, Autonomous Motion Stack' },
          { title: 'Model Based System Engineering', description: 'Simulate – Verify cycle and Build – Validate cycle' },
          { title: 'Catalog & Marketplace',          description: 'Core Products and Marketplace Ecosystem' },
          { title: 'Plug-N-Play Models',             description: 'Motion Products, Users, Application and Environment' },
          { title: 'Digital Twin',                   description: 'Remote Monitoring & Updates through Connected Edge and IoT Cloud Infrastructure' },
        ],
        stakeholders: [
          { label: 'Suppliers',    description: 'Components & Sub-systems'    },
          { label: 'OEM Partners', description: 'Digital Components Library'  },
          { label: 'Builders',     description: 'Select, Validate, Configure' },
          { label: 'Users',        description: 'Runtime & Service Portal'    },
        ],
        workflowHeading: 'Builder Workflow',
        workflowSteps: [
          'Model Selection & Customization',
          'Validate through Simulation',
          'Configure & Code Customization',
          'Testing & Validation',
          'Component Sourcing & Procurement',
          'Assembly & Firmware Programming',
          'Compliance & Production Certification',
        ],
        devHeading: 'Development Approach',
        devSteps: [
          'Software definition and design before hardware',
          'Early validation with Model based design (Azapa TDM)',
          'Standard & Stable interfaces',
          'Consume & Publish ecosystem',
        ],
        whatYouGet: {
          eyebrow: 'What you get',
          heading: 'Platform Capabilities',
          description: 'A comprehensive suite of tools and technologies designed to accelerate your motion control product development.',
        },
        stakeholdersSection: {
          eyebrow: 'Stakeholders connectivity',
          heading: 'End-to-End Integration',
          description: 'From suppliers to end users, our platform connects every stakeholder in the motion control value chain.',
        },
        devApproachSection: {
          eyebrow: 'How to build',
          description: 'Our proven methodology delivers motion control systems you can trust.',
        },
      },
      cta: {
        heading: "Let's Bring Your Motion Idea to Life.",
        description: 'Have questions about products, services, or platform integration? Our team is ready to help you build, scale, and succeed.',
        button: 'Contact us',
      },
    },
    // ── Legacy keys kept for pages that already use them ──
    hero: {
      tagline: 'Indian Innovation with Japanese Technology',
      taglineJp: '日本の技術でインドの革新',
      subtitle: 'Powering the future of motion control',
      scrollHint: 'Scroll to explore',
    },
    intro: {
      title: 'Introduction',
      heading: 'The Art of Motion',
      description: 'Takumi is a next-gen motion products company, powered by Indian innovation and Japanese technology, seamlessly blending Art & Engineering.',
      mission: 'To create a robust ecosystem for Mass Custom Engineering of Intelligent Motion Products at Speed and Scale for societal technology adoption.',
      caseTitle: 'Democratizing CASE Mobility',
      caseDescription: 'Connected – Autonomous – Shared/Smart – Electric',
      ecosystemTitle: 'Our Ecosystem',
      coCreation: 'Co-Creation Ecosystem',
      magicPlatform: 'Magic Platform',
      cloud: 'Takumi Cloud',
    },
    platform: {
      title: 'Platform',
      heading: 'Software-Defined Motion Platform',
      subtitle: 'Application Specific – Software Defined – Platform Engineered',
      features: {
        intelligentMotion: { title: 'Intelligent Motion', description: 'Application Specific – Software Defined – Platform Engineered' },
        innovativeIPs: { title: 'Innovative IPs', description: 'Motor Control Algorithms & Software, Digital Power, Sensor Fusion, Autonomous Motion Stack' },
        mbse: { title: 'Model Based System Engineering', description: 'Simulate – Verify cycle and Build – Validate cycle' },
        catalog: { title: 'Catalog & Marketplace', description: 'Core Products and Marketplace Ecosystem' },
        plugNPlay: { title: 'Plug-N-Play Models', description: 'Motion Products, Users, Application and Environment' },
        digitalTwin: { title: 'Digital Twin', description: 'Remote Monitoring & Updates through Connected Edge and IoT Cloud Infrastructure' },
      },
    },
    ecosystem: {
      title: 'Takumi Ecosystem',
      items: ['Takumi Software-Defined Motion Platform & Core Products', 'Partner Ecosystem', 'PoC & Proto Build', 'Tear-down, Benchmarking and Should-Costing', 'Value Analysis & Value Engineering', 'Motor – Controller Calibration', 'Product Qualification, Compliance Certifications and Homologation'],
    },
    products: {
      title: 'Products',
      domains: { title: 'Domains - Applications', transportation: 'Transportation', industrial: 'Industrial' },
      core: {
        title: 'Core Products',
        motors: { title: 'Motors + Motor Controllers', items: ['Brushed DC, Brushless DC, PMSM Motors and Servo Motors', 'Motor Controllers based on Infineon, TI, Renesas, Microchip', 'Ready: 12V – 96V / 100W – 33KW', 'Upcoming: 240V – 1200V / 33KW – 1200KW'] },
        controllers: { title: 'Intelligent Application Controllers', items: ['Based on Renesas, NXP and Microchip controllers', 'Smart Control for specific application, environment and users', 'Optional Telematics Unit integration for Remote Connectivity'] },
        telematics: { title: 'Telematics Unit', items: ['GPS + IMU (Accelerometer + Compass)', '4G/5G', 'WiFi + BT/BLE'] },
      },
      partner: { title: 'Partner Products', sensors: 'Sensors', hmi: 'HMI', battery: 'Battery + BMS', accessories: 'Accessories' },
      evMatrix: { title: 'EV Power Train Products' },
      intelligentMatrix: { title: 'Intelligent Motion Applications' },
    },
    partnerships: {
      title: 'Engineering Partnerships & Alliances',
      subtitle: 'Collaborating with global technology leaders',
    },
    team: {
      title: 'Our Team',
      subtitle: 'Integrated System/Product Engineering teams with specialization in Model Based System Engineering, Hardware Engineering, Software Engineering, PoC/Prototype Manufacturing, System Integration – Calibration – Validation.',
      members: {
        britto: { name: 'Britto EV', role: 'Founder & MD', bio: 'Electrical Engineer 20+ years with Japanese companies. Well connected with the Japanese motor ecosystem. Believes in Indian innovation.' },
        aravind: { name: 'Aravind Prasath', role: 'Founder', bio: '20+ years in power electronics and motor controls. An innovator, he created the BLDC ceiling fan solution which has more than 50% share in India.' },
        prasanth: { name: 'Prasanth B', role: 'Founder', bio: 'Vehicle engineer for 12 years, who professes model based design. He has worked on the models for many electric vehicle transmission systems.' },
        mohara: { name: 'Mohara Toshinari', role: 'Director', bio: 'Seasoned powertrain engineer, who joined the FCC board from Honda R&D. A visionary leader, currently he heads the CASE initiative.' },
        shimogaki: { name: 'Shimogaki Y', role: 'Advisor', bio: 'Seasoned powertrain engineer, who joined the FCC board from Honda R&D. A visionary leader, currently he heads the CASE initiative.' },
      },
    },
    facilities: {
      title: 'Facilities & Certifications',
      locations: {
        kyoto: { name: 'Takumi – Kyoto', description: 'Technology Partnerships – Market Research' },
        bangalore: { name: 'Takumi – Bangalore', description: 'Engineering and PoC & Proto Production' },
        tenkasi: { name: 'Takumi – Tenkasi', description: 'Engineering, Production & Test Lab' },
      },
      certifications: ['ISO 9001:2015', 'ISO 14001:2015', 'ISO 27001:2013', 'ESD Association'],
    },
    contact: {
      title: 'Get In Touch',
      subtitle: "Let's discuss how we can power your vision",
      form: {
        name: 'Full Name',
        email: 'Email Address',
        company: 'Company',
        subject: 'Subject',
        message: 'Your Message',
        submit: 'Send Message',
        sending: 'Sending…',
        success: 'Message sent successfully!',
        error: 'Failed to send message. Please try again.',
      },
      sendMessage: 'Send us a message',
      contactInfo: 'Contact information',
      indiaHead: 'India Headquarters',
      japanHQ: 'Japan Headquarters',
      successMsg: "Message sent! We'll get back to you shortly.",
      errorMsg: 'Something went wrong. Please try again or email us directly.',
    },
  },

  // ═══════════════════════════════════════════════════════════
  //  JAPANESE
  // ═══════════════════════════════════════════════════════════
  jp: {
    nav: {
      home: 'ホーム',
      ecosystem: 'エコシステム',
      about: '会社概要',
      contact: 'お問い合わせ',
      dropdown: {
        products: '製品',
        services: 'サービス',
        platform: 'ソフトウェア定義モーションプラットフォーム',
        applications: 'ドメイン＆アプリケーション',
        company: '会社案内',
        team: 'チーム',
        facilities: '施設',
        partners: 'パートナー',
      },
    },
    footer: {
      quickLinks: 'クイックリンク',
      socials: 'ソーシャル',
      links: {
        home: 'ホーム',
        ecosystem: 'エコシステム',
        about: '会社概要',
        contact: 'お問い合わせ',
      },
      copyright: '© 2026 Takumi Motion Controls Pvt. Ltd. 無断転載を禁じます。',
      terms: '利用規約',
      privacy: 'プライバシーポリシー',
      japanAddress: ['〒600-8223', '京都府京都市下京区大黒町227', '第2京都ビル402号室'],
    },
    home: {
      hero: {
        line1: 'モーション制御で',
        line2: '未来を切り拓く',
        subtitle: 'インドのイノベーションと日本の技術が生み出す価値',
        explore: 'ソリューションを探る',
        getInTouch: 'お問い合わせ',
      },
      artOfMotion: {
        eyebrow: '私たちの取り組み',
        heading: 'モーション制御の匠',
        description: 'Takumiは、インドのイノベーションと日本の技術を融合した次世代のモーションソリューション企業です。',
        cta: '私たちのストーリー',
      },
      case: {
        eyebrow: '私たちの信念',
        heading: 'CASEモビリティの民主化',
        description: 'CASEモビリティの可能性を、より多くの人へ。Takumiは、誰もが利用できる持続可能なモビリティ社会の実現に向けて、先進的なモーション制御技術を開発しています。',
        connect: 'Connected',
        autonomous: 'Autonomous',
        shared: 'Shared/Smart',
        electric: 'Electric',
      },
      ecosystem: {
        eyebrow: '私たちのエコシステム',
        heading: 'モビリティの未来を動かす',
        description: 'Takumiが構築する製品、サービス、アプリケーション、プラットフォームは、すべてが連携する一つのモーション制御エコシステムとして設計されています。モビリティの未来を支える技術をご覧ください。',
        products: { title: '製品', tags: ['HVACコントローラー', 'EVコントローラー', 'T1コントローラー', 'HMIコントローラー', 'コントローラー'] },
        platform: { title: 'ソフトウェア定義モーションプラットフォーム', tags: ['インテリジェントモーション', '革新的なIP', 'プラグアンドプレイモデル'] },
        domains: { title: 'ドメイン＆アプリケーション', tags: ['EV', 'ドローン', 'フォークリフト', 'コンベヤー'] },
        services: { title: 'サービス', tags: ['PoC＆プロトタイプ製作', 'モーターコントローラーキャリブレーション', '分解＆ベンチマーキング', '価値分析＆価値エンジニアリング'] },
      },
      transport: {
        eyebrow: '私たちのコア',
        heading: 'あらゆるアプリケーションに対応する高精度モーション制御',
        description: '幅広い業界で採用。豊富な経験に基づく設計開発。そのすべてを支えるモーション制御のエキスパート企業です。',
        sectionTitle: '交通アプリケーション',
        seeAll: 'すべて見る',
        apps: ['電動スクーター', '電動サイクル', '電気自動車', '電動トゥクトゥク', 'フォークリフト', 'ドローン'],
      },
      industrial: {
        sectionTitle: '産業アプリケーション',
        seeAll: 'すべて見る',
        apps: ['AGV/AMR', 'ロボットアーム', 'コンベヤー', 'トレッドミル', 'テレコムRET', '産業用ポンプ'],
      },
      whatsNew: {
        eyebrow: '最新情報',
        heading: '明日のモビリティのための精密エンジニアリング',
        description: '電気自動車から産業自動化まで、私たちのモーション制御ソリューションは、輸送と製造分野の未来を支える技術を提供しています。',
        powerRange: '対応パワーレンジ',
        voltageRange: '予定電圧レンジ',
        cta: 'ソリューションを探る',
      },
      partners: {
        investors: '投資家',
        techPartners: 'テクノロジーパートナー',
        clients: 'クライアント',
      },
      cta: {
        heading: 'ビジョンを実現する準備はできていますか？',
        description: '製品、プラットフォーム、パートナーシップ — どんなことでもご相談ください。',
        button: 'お問い合わせ',
      },
    },
    about: {
      banner: {
        heading1: 'Takumi',
        heading2: 'について',
        description: 'Takumiは、インドのイノベーションと日本の技術力を融合した次世代のモーション制御企業です。',
      },
      mission: {
        title: '私たちのミッション',
        quote: 'インテリジェントモーション製品の大規模かつ効率的な開発を支えるエコシステムを構築し、先進技術の社会実装を加速する。',
        belief: '私たちは、インドのイノベーションと日本の技術を組み合わせることの力を信じており、モーション制御エンジニアリングのグローバルスタンダードを満たすだけでなく超える解決策を生み出します。',
      },
      vision: {
        title: '私たちのビジョン',
        description: '従来の分断された開発・流通プロセスを見直し、よりアクセスしやすく効率的なモビリティエコシステムを実現する。',
      },
      case: {
        title: 'CASEモビリティの民主化',
        connect: 'Connected',
        autonomous: 'Autonomous',
        shared: 'Shared/Smart',
        electric: 'Electric',
      },
      team: {
        eyebrow: 'チーム',
        heading: 'Takumiのチーム',
        description: 'モデルベースシステムエンジニアリング、ハードウェアエンジニアリング、ソフトウェアエンジニアリング、PoC/プロトタイプ製造、システム統合 – キャリブレーション – 検証に特化した統合システム/製品エンジニアリングチーム。',
        peopleEyebrow: '人材',
        peopleHeading: '40名以上のプロフェッショナルがモーションの未来を創る',
        peopleDescription: '一つのミッションに結ばれたチーム：インテリジェントモーション制御をより多くの人に届けること。多様な才能、数十年の総合的な専門知識、そして重要な製品を構築するという揺るぎないコミットメントを持ち寄っています。',
      },
      facilities: {
        eyebrow: '施設',
        heading: '拠点紹介',
        description: 'コンセプトから生産まで、私たちの施設はモーション制御製品開発のすべての段階をサポートします。',
        upcoming: '近日公開',
        locations: [
          { name: 'タクミ ベンガルール', badge: 'ベンガルール、インド', desc: 'エンジニアリングとPoC・試作開発' },
          { name: 'タクミ 京都',         badge: '京都、日本',           desc: '技術パートナーシップ・市場調査' },
          { name: 'タクミ テンカシ',     badge: 'テンカシ、インド',     desc: 'エンジニアリング、生産＆テストラボ' },
          { name: 'タクミ チェンナイ',   badge: 'チェンナイ、インド',   desc: '近日公開' },
        ],
      },
      partners: {
        eyebrow: 'パートナー',
        heading: 'スケールアップのための協業',
        description: '私たちは自動車、ロボティクス、テクノロジーの業界リーダーと協力して、能力を拡大し、新市場に参入し、複雑な課題を解決します。',
        semiconductor: '半導体パートナー',
        software: 'ソフトウェア＆ツールパートナー',
        investors: 'Takumiへの出資企業',
        fccYear: '2022年',
        fccDesc: '株式会社エフ・シー・シーはTakumi Motion Controls Pvt. Ltd.に初期段階の戦略的投資を行いました。',
        zohoYear: '2026年',
        zohoDesc: 'Zoho Corporation Pvt. Ltd.はTakumi Motion Controls Pvt. Ltd.に戦略的投資を行いました。',
      },
      cta: {
        heading: '一緒に働きませんか',
        description: 'メッセージをお送りください。チームがすぐにご連絡いたします。',
        button: 'お問い合わせ',
      },
    },
    ecosystemPage: {
      hero: {
        line1: 'モーションの未来を',
        line2: '支えるのは',
        subtitle: 'すべてが連携し、一体となって機能するモーション制御エコシステム。',
        rotatingWords: ['製品', 'サービス', 'プラットフォーム', 'アプリケーション'],
      },
      tabs: {
        products: '製品',
        services: 'サービス',
        applications: 'ドメイン＆アプリケーション',
        platform: 'ソフトウェア定義モーションプラットフォーム',
      },
      products: {
        eyebrow: '製品',
        heading: 'コア製品',
        description: '自社開発のモーション制御製品は、ソフトウェア定義モーションプラットフォームによって支えられています。',
        viewDetails: '詳細を見る',
        partnerTitle: 'パートナー製品一覧',
        partnerDescription: 'モーション制御ソリューションを補完するパートナー企業の製品群です。',
        coreProducts: [
          {
            kicker: 'モーターコントローラー',
            title: 'インテリジェントビークルコントロールユニット (iVCU)',
            description: 'iVCUはビークルの中枢で、モーター制御、安全インターロック、リアルタイムのシステム全体の診断を調整します。',
            tags: ['二輪車', '三輪車両', '農業用車両', '軽オフロード'],
          },
          {
            kicker: 'テレマティクスコントローラー',
            title: 'テレマティクスコントロールユニット (TCU)',
            description: 'TCUはコンパクトな組み込みゲートウェイで、接続された二輪車をライブデータソースに変換し、モーション、GNSS位置、車両状態を取得してLTE/MQTTでタクミクラウドにストリーミングします。',
            tags: ['二輪車', '三輪車両', '軽オフロード', 'フリートとロジスティクス'],
          },
          {
            kicker: 'モーター＋モーターコントローラー',
            title: 'モーターコントロールユニット (MCU)',
            description: 'MCUは広い電圧/パワー範囲でセンサーレスおよびエンコーダーベースのモーター制御を提供し、高効率のソフトウェア定義ドライブシステム向けに構築されています。',
            tags: ['二輪車', '三輪車両', '軽オフロード', 'フリートとロジスティクス'],
          },
        ],
        partnerProducts: [
          { title: 'Sensor',                            items: ['ライダー', 'レーダー', '超音波', '位置センサー', '他2項目'] },
          { title: 'HMI (Human Machine Interface)',     items: ['コンソール/ディスプレイ', 'ブレーキ', 'スロットル', 'ステアリング', '他2項目'] },
          { title: 'Battery + BMS',                    items: ['最大400V / 100kWh', 'リチウムイオン', 'スマートBMS'] },
          { title: 'Accessories',                      items: ['カスタムワイヤーハーネス', 'エンクロージャー', 'コネクター'] },
        ],
      },
      services: {
        eyebrow: 'サービス',
        heading: 'コアサービス',
        description: '製品開発を加速するエンドツーエンドのエンジニアリングサービス。',
        items: [
          { title: 'タクミソフトウェア定義モーションプラットフォーム', description: 'モーション制御製品開発のためのプラットフォーム。',              features: ['デジタルツイン', 'モデルベース設計', 'オンラインIDE', 'ランタイムサービス'] },
          { title: 'PoC＆プロトタイプ製作',               description: '迅速なプロトタイピングとPoC開発。',                                           features: ['高速ターンアラウンド', '設計検証', 'イテレーションサポート', '量産対応'] },
          { title: 'モーターコントローラーキャリブレーション', description: '最適なパフォーマンスのためのエキスパートキャリブレーションサービス。',        features: ['パフォーマンスチューニング', '効率最適化', '熱管理', '安全準拠'] },
          { title: '分解＆ベンチマーキング',              description: '競合分析と製品ベンチマーキング。',                                               features: ['コンポーネント分析', 'コスト内訳', '技術評価', '改善提案'] },
          { title: '価値分析＆価値エンジニアリング',       description: '品質を維持しながらコストを最適化。',                                             features: ['原価計算', '設計最適化', 'サプライヤー交渉', 'BOM削減'] },
          { title: '製品認定＆認証',                      description: 'パートナーネットワークによる認証とホモロゲーションサポート。',                    features: ['適合性テスト', '規制認証', 'ホモロゲーション', 'ドキュメント'] },
        ],
      },
      applications: {
        eyebrow: 'ドメイン＆アプリケーション',
        transportTitle: '交通アプリケーション',
        industrialTitle: '産業アプリケーション',
        seeAll: 'すべて見る',
        transportApps: ['電動スクーター', '電動自転車', '電気自動車', '電動トゥクトゥク', 'フォークリフト', 'ドローン'],
        industrialApps: ['AGV/AMR', 'ロボットアーム', 'コンベヤー', 'トレッドミル', 'テレコムRET', '産業用ポンプ'],
      },
      platform: {
        eyebrow: 'ソフトウェア定義モーションプラットフォーム',
        heading: '一つのプラットフォーム、\n無限のモーションの可能性。',
        description: 'ソフトウェア定義モーションプラットフォームは、シミュレーション環境、ソフトウェア開発機能、リアルタイムサービスを統合し、モーション制御開発のための包括的なプラットフォームを提供します。',
        features: [
          { title: 'インテリジェントモーション',             description: 'アプリケーション固有 – ソフトウェア定義 – プラットフォームエンジニアリング' },
          { title: '革新的なIP',                           description: 'モーター制御アルゴリズム＆ソフトウェア、デジタルパワー、センサーフュージョン、自律モーションスタック' },
          { title: 'モデルベースシステムエンジニアリング',  description: 'シミュレーションと検証、開発と妥当性確認のサイクルを支援' },
          { title: 'カタログ＆マーケットプレイス',         description: 'コア製品とマーケットプレイスエコシステム' },
          { title: 'プラグアンドプレイモデル',             description: 'モーション製品、ユーザー、アプリケーション、環境' },
          { title: 'デジタルツイン',                       description: 'コネクテッドエッジとIoTクラウドインフラストラクチャによるリモートモニタリングとアップデート' },
        ],
        stakeholders: [
          { label: 'サプライヤー',   description: 'コンポーネント＆サブシステム'  },
          { label: 'OEMパートナー', description: 'デジタルコンポーネントライブラリ' },
          { label: '開発者',         description: '選択、検証、設定'              },
          { label: 'ユーザー',       description: 'ランタイム＆サービスポータル'  },
        ],
        workflowHeading: '開発ワークフロー',
        workflowSteps: [
          'モデル選択＆カスタマイズ',
          'シミュレーションによる検証',
          '設定＆コードカスタマイズ',
          'テスト＆検証',
          'コンポーネント調達＆購買',
          '組み立て＆ファームウェアプログラミング',
          '規格適合・量産認証',
        ],
        devHeading: '開発アプローチ',
        devSteps: [
          'ハードウェア開発前にソフトウェアを設計・定義',
          'モデルベース設計による早期検証（Azapa TDM）',
          '標準＆安定したインターフェース',
          'エコシステムの活用と共有',
        ],
        whatYouGet: {
          eyebrow: 'あなたが得るもの',
          heading: 'プラットフォームの機能',
          description: 'モーション制御製品開発を加速するために設計された、包括的なツールと技術のプラットフォーム。',
        },
        stakeholdersSection: {
          eyebrow: 'ステークホルダーの連携',
          heading: 'エンドツーエンドの統合',
          description: 'サプライヤーからエンドユーザーまで、プラットフォームはモーション制御のバリューチェーン全体のステークホルダーをつなぎます。',
        },
        devApproachSection: {
          eyebrow: '構築方法',
          description: '実証済みの方法論で、信頼性の高いモーション制御システムを実現します。',
        },
      },
      cta: {
        heading: 'モーションのアイデアを実現しましょう。',
        description: '製品、サービス、またはプラットフォーム統合に関するご質問はありますか？私たちのチームがビルド、スケール、成功をお手伝いします。',
        button: 'お問い合わせ',
      },
    },
    // ── Legacy keys kept ──
    hero: {
      tagline: '日本の技術とインドのイノベーション',
      taglineJp: 'Indian Innovation with Japanese Technology',
      subtitle: 'モーションコントロールの未来を動かす',
      cta: 'ビジョンを発見',
      scrollHint: 'スクロールして探索',
    },
    intro: {
      title: '紹介',
      heading: 'モーションの芸術',
      description: 'タクミは、インドのイノベーションと日本の技術を融合し、アートとエンジニアリングをシームレスに融合させた次世代モーション製品会社です。',
      mission: '社会的な技術採用のためのインテリジェントモーション製品のマスカスタムエンジニアリングのための強力なエコシステムを構築すること。',
      caseTitle: 'CASEモビリティの民主化',
      caseDescription: 'コネクテッド – 自律 – シェアード/スマート – 電気',
      ecosystemTitle: 'エコシステム',
      coCreation: '共創エコシステム',
      magicPlatform: 'マジックプラットフォーム',
      cloud: 'タクミクラウド',
    },
    platform: {
      title: 'プラットフォーム',
      heading: 'ソフトウェア定義モーションプラットフォーム',
      subtitle: 'アプリケーション固有 – ソフトウェア定義 – プラットフォームエンジニアリング',
      features: {
        intelligentMotion: { title: 'インテリジェントモーション', description: 'アプリケーション固有 – ソフトウェア定義 – プラットフォームエンジニアリング' },
        innovativeIPs: { title: '革新的なIP', description: 'モーター制御アルゴリズム＆ソフトウェア、デジタルパワー、センサーフュージョン、自律モーションスタック' },
        mbse: { title: 'モデルベースシステムエンジニアリング', description: 'シミュレーションと検証、開発と妥当性確認のサイクルを支援' },
        catalog: { title: 'カタログ＆マーケットプレイス', description: 'コア製品とマーケットプレイスエコシステム' },
        plugNPlay: { title: 'プラグアンドプレイモデル', description: 'モーション製品、ユーザー、アプリケーション、環境' },
        digitalTwin: { title: 'デジタルツイン', description: 'コネクテッドエッジとIoTクラウドインフラストラクチャによるリモートモニタリングとアップデート' },
      },
    },
    ecosystem: {
      title: 'タクミエコシステム',
      items: ['タクミソフトウェア定義モーションプラットフォーム＆コア製品', 'パートナーエコシステム', 'PoC＆プロトビルド', '分解、ベンチマーキング、コスト計算', '価値分析＆価値エンジニアリング', 'モーター – コントローラーキャリブレーション', '製品認定、コンプライアンス認証、ホモロゲーション'],
    },
    products: {
      title: '製品',
      domains: { title: 'ドメイン - アプリケーション', transportation: '輸送', industrial: '産業' },
      core: {
        title: 'コア製品',
        motors: { title: 'モーター + モーターコントローラー', items: ['ブラシDC、ブラシレスDC、PMSMモーター、サーボモーター', 'Infineon、TI、Renesas、Microchipベースのモーターコントローラー', '対応：12V – 96V / 100W – 33KW', '予定：240V – 1200V / 33KW – 1200KW'] },
        controllers: { title: 'インテリジェントアプリケーションコントローラー', items: ['Renesas、NXP、Microchipコントローラーベース', '特定のアプリケーション、環境、ユーザー向けのスマートコントロール', 'リモート接続用のオプションのテレマティクスユニット統合'] },
        telematics: { title: 'テレマティクスユニット', items: ['GPS + IMU（加速度計 + コンパス）', '4G/5G', 'WiFi + BT/BLE'] },
      },
      partner: { title: 'パートナー製品', sensors: 'センサー', hmi: 'HMI', battery: 'バッテリー + BMS', accessories: 'アクセサリー' },
      evMatrix: { title: 'EVパワートレイン製品' },
      intelligentMatrix: { title: 'インテリジェントモーションアプリケーション' },
    },
    partnerships: {
      title: 'エンジニアリングパートナーシップ＆アライアンス',
      subtitle: 'グローバルテクノロジーリーダーとの協力',
    },
    team: {
      title: 'チーム',
      subtitle: 'モデルベースシステムエンジニアリング、ハードウェアエンジニアリング、ソフトウェアエンジニアリング、PoC/プロトタイプ製造、システム統合 – キャリブレーション – 検証に特化した統合システム/製品エンジニアリングチーム。',
      members: {
        britto: { name: 'ブリット EV', role: '創業者 & 社長', bio: '日本企業で20年以上の経験を持つ電気エンジニア。日本のモーターエコシステムとの強いつながりを持つ。インドのイノベーションを信じる。' },
        aravind: { name: 'アラビンド・プラサス', role: '創業者', bio: 'パワーエレクトロニクスとモーター制御で20年以上の経験。インドで50%以上のシェアを持つBLDC天井扇風機ソリューションを作成したイノベーター。' },
        prasanth: { name: 'プラサンス B', role: '創業者', bio: 'モデルベース設計を専門とする12年間の車両エンジニア。多くの電気自動車トランスミッションシステムのモデルに取り組んできた。' },
        mohara: { name: '茂原 敏成', role: '取締役', bio: 'ホンダR&DからFCCボードに参加した経験豊富なパワートレインエンジニア。先見の明のあるリーダーで、現在CASEイニシアチブを率いている。' },
        shimogaki: { name: '下垣 Y', role: 'アドバイザー', bio: 'ホンダR&DからFCCボードに参加した経験豊富なパワートレインエンジニア。先見の明のあるリーダーで、現在CASEイニシアチブを率いている。' },
      },
    },
    facilities: {
      title: '施設＆認証',
      locations: {
        kyoto: { name: 'タクミ – 京都', description: '技術パートナーシップ – 市場調査' },
        bangalore: { name: 'タクミ – バンガロール', description: 'エンジニアリングとPoC・試作開発' },
        tenkasi: { name: 'タクミ – テンカシ', description: 'エンジニアリング、生産＆テストラボ' },
      },
      certifications: ['ISO 9001:2015', 'ISO 14001:2015', 'ISO 27001:2013', 'ESD協会'],
    },
    contact: {
      title: 'お問い合わせ',
      subtitle: 'お客様のビジョン実現についてご相談ください',
      form: {
        name: 'お名前',
        email: 'メールアドレス',
        company: '会社名',
        subject: '件名',
        message: 'メッセージ',
        submit: '送信',
        sending: '送信中...',
        success: 'メッセージが送信されました！',
        error: '送信に失敗しました。もう一度お試しください。',
      },
      sendMessage: 'メッセージを送る',
      contactInfo: 'お問い合わせ先情報',
      indiaHead: 'インド本社（India Headquarters）',
      japanHQ: '日本本社',
      successMsg: 'メッセージが送信されました！すぐにご連絡いたします。',
      errorMsg: '問題が発生しました。再度お試しいただくか、直接メールにてご連絡ください。',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    localStorage.setItem('takumi-language', language);
    document.documentElement.lang = language === 'jp' ? 'ja' : 'en';
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value !== undefined ? value : key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'jp' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
